import React from "react"
import { useGetMedicalInfo, useMedicalInfo } from "../../context/MedicalInfoContext"
import { useStateMachine } from "../../../context/stateMachine/StateMachineV2"
import {
  StepLayout,
  SkipButton,
  ButtonFooterLayout,
  ContinueButton,
  RoutingFlowTitle
} from "#/components/carrot-plans/shared"
import { defineMessage, FormattedMessage, MessageDescriptor, useIntl } from "react-intl"
import { Form, FormTextField } from "@carrotfertility/carotene-core-x"
import { InputAdornment, Stack, useTheme } from "@carrotfertility/carotene-core"
import { TextDirection } from "#/types/Locale"
import { useTextDirection } from "#/services/locale/hooks/useTextDirection"

type FormValues = {
  height: {
    measurementOne: string
    measurementTwo: string
  }
  weight: string
}

type HeightFieldName = "height" | "prePregnancyHeight"
type HeightUnitsFieldName = "heightUnits" | "prePregnancyHeightUnits"
type WeightFieldName = "weight" | "prePregnancyWeight"
type WeightUnitsFieldName = "weightUnits" | "prePregnancyWeightUnits"
type SkipKey = "heightWeight" | "prePregnancyHeightWeight"

type HeightWeightProps = {
  headerText: MessageDescriptor
  heightField: HeightFieldName
  heightUnitsField: HeightUnitsFieldName
  weightField: WeightFieldName
  weightUnitsField: WeightUnitsFieldName
  skipKey: SkipKey
}

function HeightWeight({
  headerText,
  heightField,
  heightUnitsField,
  weightField,
  weightUnitsField,
  skipKey
}: HeightWeightProps): JSX.Element {
  const intl = useIntl()
  const theme = useTheme()
  const { updateMedicalInfo, hasQuestionBeenSkipped } = useMedicalInfo()
  const { data: medicalInfo } = useGetMedicalInfo()
  const textDirection = useTextDirection()
  const isRtl = textDirection === TextDirection.RightToLeft
  const isLtr = textDirection === TextDirection.LeftToRight

  const heightValue = medicalInfo?.[heightField]
  const weightValue = medicalInfo?.[weightField]
  const defaultHeightMeasurementOne = heightValue ? Math.floor(parseInt(heightValue.toString(), 10) / 12) : null
  const defaultHeightMeasurementTwo = heightValue ? parseInt(heightValue.toString(), 10) % 12 : null

  const { send } = useStateMachine(weightValue || heightValue || hasQuestionBeenSkipped(skipKey) ? "" : null)

  const header = intl.formatMessage(headerText)

  async function onClickContinue(values: FormValues): Promise<void> {
    if (!values?.height.measurementOne && !values?.height.measurementTwo && !values?.weight) {
      await onSkip()
    } else {
      const heightMeasurementOne = parseInt(values?.height.measurementOne || "0", 10)
      const heightMeasurementTwo = parseInt(values?.height.measurementTwo || "0", 10)
      const weight = parseFloat(values?.weight || "0")

      const medicalInfoPayload = {
        [heightField]: heightMeasurementOne * 12 + heightMeasurementTwo,
        [heightUnitsField]: "IN",
        [weightField]: weight,
        [weightUnitsField]: "LB"
      }

      await updateMedicalInfo(medicalInfoPayload)
      send("")
    }
  }

  async function onSkip(): Promise<void> {
    await updateMedicalInfo({ skipFields: [skipKey] })
    send("")
  }

  return (
    <>
      <RoutingFlowTitle title={header} />
      <StepLayout
        header={header}
        formElements={
          <Form
            defaultValues={{
              height: {
                measurementOne: defaultHeightMeasurementOne?.toString(),
                measurementTwo: defaultHeightMeasurementTwo?.toString()
              },
              weight: weightValue?.toString()
            }}
            onSubmit={onClickContinue}
          >
            <Stack
              marginBottom={theme.spacing(theme.tokens.spacing.lg)}
              spacing={theme.spacing(theme.tokens.spacing.xs)}
            >
              <FormTextField
                InputLabelProps={{ "aria-hidden": true }}
                label={intl.formatMessage({ defaultMessage: "Height" })}
                id="heightFtInput"
                name="height.measurementOne"
                type="number"
                registerOptions={{
                  max: 10,
                  min: 1
                }}
                inputProps={{
                  max: 10,
                  min: 1,
                  "aria-label": intl.formatMessage({ defaultMessage: "height in feet" })
                }}
                InputProps={{
                  endAdornment: isLtr ? (
                    <InputAdornment position="end">
                      <FormattedMessage defaultMessage="ft" description="Abbreviation for feet" />
                    </InputAdornment>
                  ) : null,
                  startAdornment: isRtl ? (
                    <InputAdornment position="start">
                      <FormattedMessage defaultMessage="ft" description="Abbreviation for feet" />
                    </InputAdornment>
                  ) : null
                }}
              />
              <FormTextField
                id="heightInInput"
                name="height.measurementTwo"
                type="number"
                registerOptions={{
                  max: 11,
                  min: 0
                }}
                inputProps={{
                  max: 11,
                  min: 0,
                  "aria-label": intl.formatMessage({ defaultMessage: "height in inches" })
                }}
                InputProps={{
                  endAdornment: isLtr ? (
                    <InputAdornment position="end">
                      <FormattedMessage defaultMessage="in" description="Abbreviation for inches" />
                    </InputAdornment>
                  ) : null,
                  startAdornment: isRtl ? (
                    <InputAdornment position="start">
                      <FormattedMessage defaultMessage="in" description="Abbreviation for inches" />
                    </InputAdornment>
                  ) : null
                }}
              />
            </Stack>
            <FormTextField
              label={intl.formatMessage({ defaultMessage: "Weight" })}
              InputLabelProps={{ "aria-hidden": true }}
              id={"weightInput"}
              name="weight"
              type="number"
              registerOptions={{
                max: 999,
                min: 0
              }}
              inputProps={{
                max: 999,
                min: 0,
                step: 0.1,
                "aria-label": intl.formatMessage({ defaultMessage: "weight in pounds" })
              }}
              InputProps={{
                endAdornment: isLtr ? (
                  <InputAdornment position="end">
                    <FormattedMessage defaultMessage="lbs" description="Abbreviation for pounds (weight measurement)" />
                  </InputAdornment>
                ) : null,
                startAdornment: isRtl ? (
                  <InputAdornment position="start">
                    <FormattedMessage defaultMessage="lbs" description="Abbreviation for pounds (weight measurement)" />
                  </InputAdornment>
                ) : null
              }}
            />
            <ButtonFooterLayout
              continueButton={
                <ContinueButton>
                  <FormattedMessage defaultMessage="Continue" />
                </ContinueButton>
              }
              skipButton={<SkipButton onClick={onSkip} />}
            />
          </Form>
        }
      />
    </>
  )
}

export const HeightWeightStep = (): JSX.Element => (
  <HeightWeight
    headerText={defineMessage({ defaultMessage: "Please enter your height & weight." })}
    heightField="height"
    heightUnitsField="heightUnits"
    weightField="weight"
    weightUnitsField="weightUnits"
    skipKey="heightWeight"
  />
)

export const PrePregnancyHeightWeightStep = (): JSX.Element => (
  <HeightWeight
    headerText={defineMessage({ defaultMessage: "Please enter your pre-pregnancy height and weight." })}
    heightField="prePregnancyHeight"
    heightUnitsField="prePregnancyHeightUnits"
    weightField="prePregnancyWeight"
    weightUnitsField="prePregnancyWeightUnits"
    skipKey="prePregnancyHeightWeight"
  />
)
